/**
 * AccountNavigationStore
 *
 * Zustand store for tracking the path the user was on before entering
 * the account pages. Used by the account sidebar back button to return
 * the user to their previous location.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface AccountNavigationState {
  /** The last path visited before entering /account/* */
  returnPath: string;
}

interface AccountNavigationActions {
  /** Update the return path (called when user is on a non-account page) */
  setReturnPath: (path: string) => void;
  /** Get the return path (defaults to '/') */
  getReturnPath: () => string;
}

type AccountNavigationStore = AccountNavigationState & AccountNavigationActions;

const DEFAULT_PATH = '/';

export const useAccountNavigationStore = create<AccountNavigationStore>()(
  devtools(
    (set, get) => ({
      returnPath: DEFAULT_PATH,

      setReturnPath: (path: string) => {
        set({ returnPath: path }, false, 'setReturnPath');
      },

      getReturnPath: () => {
        return get().returnPath || DEFAULT_PATH;
      }
    }),
    {
      name: 'AccountNavigationStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

/** Selector hook for the return path */
export const useAccountReturnPath = () =>
  useAccountNavigationStore((s) => s.returnPath);

/** Non-hook getter for use outside components */
export const getAccountNavigationActions = () => ({
  setReturnPath: useAccountNavigationStore.getState().setReturnPath,
  getReturnPath: useAccountNavigationStore.getState().getReturnPath
});
